import { UseMutationOptions } from "@tanstack/react-query";
import { useMutationApi, useQueryApi } from "api/hooks.ts";
import { composeGetIncomingPostbackQueryOptions } from "api/TenantSlug/IncomingPostback/queryOptions.ts";
import { AxiosError } from "axios";
import { ApiErrorResponse } from "types/models.ts";

import { incomingPostbackApi } from "./api";

import GetIncomingPostbacksResponse = App.Http.Responses.Tracking.GetIncomingPostbacksResponse;
import GetIncomingPostbackResponse = App.Http.Responses.Tracking.GetIncomingPostbackResponse;
import StoreIncomingPostbackRequest = App.Http.Requests.Tracking.StoreIncomingPostbackRequest;
import GetConnectionSequenceStepsExportResponse = App.Http.Responses.ConnectionCreator.SequenceStep.GetConnectionSequenceStepsExportResponse;
import GetIncomingPostbacksRequest = App.Http.Requests.Tracking.GetIncomingPostbacksRequest;
import StoreIncomingPostbackConnectionSequenceStepsRequest = App.Http.Requests.Tracking.StoreIncomingPostbackConnectionSequenceStepsRequest;
import UpdateIncomingPostbackRequest = App.Http.Requests.Tracking.UpdateIncomingPostbackRequest;
import BaseExportRequest = App.Http.Requests.BaseExportRequest;
import ExportResponse = App.Http.Responses.ExportResponse;
import TestIncomingPostbackRequest = App.Http.Requests.Tracking.TestIncomingPostbackRequest;
import TestConnectionSequenceStepResponse = App.Http.Responses.ConnectionSequenceStep.TestConnectionSequenceStepResponse;

// Query to get a list of tracking postbacks
export const useGetIncomingPostbacksQuery = (request: GetIncomingPostbacksRequest, options = {}) => {
    return useQueryApi<GetIncomingPostbacksResponse>({
        queryKey: ["getIncomingPostbacks", request],
        queryFn: () => incomingPostbackApi.index(request),
        ...options,
    });
};

// Query to get a specific tracking postback
export const useGetIncomingPostbackQuery = (postbackId: string, request: unknown, options = {}) => {
    return useQueryApi<GetIncomingPostbackResponse>({
        ...composeGetIncomingPostbackQueryOptions(postbackId, request),
        ...options,
    });
};

// Mutation to store a new tracking postback
export const useStoreIncomingPostbackMutation = (
    options: UseMutationOptions<
        GetIncomingPostbackResponse,
        AxiosError<ApiErrorResponse>,
        StoreIncomingPostbackRequest
    > = {}
) => {
    return useMutationApi<GetIncomingPostbackResponse, StoreIncomingPostbackRequest>({
        mutationKey: ["storeIncomingPostback"],
        mutationFn: (request: StoreIncomingPostbackRequest) => incomingPostbackApi.store(request),
        ...options,
    });
};

// Mutation to update an existing tracking postback
export const useUpdateIncomingPostbackMutation = (id: string, options = {}) => {
    return useMutationApi<GetIncomingPostbackResponse, UpdateIncomingPostbackRequest>({
        mutationKey: ["updateIncomingPostback"],
        mutationFn: (request: UpdateIncomingPostbackRequest) => incomingPostbackApi.update(request, id),
        ...options,
    });
};

// Mutation to destroy a tracking postback
export const useDestroyIncomingPostbackMutation = (options = {}) => {
    return useMutationApi<void, string>({
        mutationKey: ["destroyIncomingPostback"],
        mutationFn: (postbackId: string) => incomingPostbackApi.destroy(postbackId),
        ...options,
    });
};

// Query to get a specific IncomingPostback in export format
export const useGetIncomingPostbackExportQuery = (
    incomingPostbackId: string,
    request?: GetIncomingPostbacksRequest,
    options = {}
) => {
    return useQueryApi<GetIncomingPostbackResponse>({
        queryKey: ["getIncomingPostbackExport", incomingPostbackId],
        queryFn: () => incomingPostbackApi.showExport(incomingPostbackId, request),
        ...options,
    });
};

// Query to get a specific IncomingPostback connection sequence steps in export format
export const useGetIncomingPostbackMethodsExportQuery = (
    incomingPostbackId: string,
    request?: GetIncomingPostbacksRequest,
    options = {}
) => {
    return useQueryApi<GetConnectionSequenceStepsExportResponse>({
        queryKey: ["getIncomingPostbackMethodsExport", incomingPostbackId],
        queryFn: () => incomingPostbackApi.showMethodsExport(incomingPostbackId, request),
        ...options,
    });
};

// Mutation to update an existing IncomingPostback methods using PUT
export const useUpdateIncomingPostbackMethodsMutation = (incomingPostbackId: string, options = {}) => {
    return useMutationApi<
        GetConnectionSequenceStepsExportResponse,
        StoreIncomingPostbackConnectionSequenceStepsRequest
    >({
        mutationKey: ["updateIncomingPostbackMethods", incomingPostbackId],
        mutationFn: (request: StoreIncomingPostbackConnectionSequenceStepsRequest) =>
            incomingPostbackApi.updateMethods(request, incomingPostbackId),
        ...options,
    });
};

export const useExportIncomingPostbacksMutation = (
    options: UseMutationOptions<ExportResponse, AxiosError<ApiErrorResponse>, BaseExportRequest> = {}
) => {
    return useMutationApi<ExportResponse, BaseExportRequest>({
        mutationKey: ["exportIncomingPostbacks"],
        mutationFn: (request) => incomingPostbackApi.export(request),
        ...options,
    });
};

// Mutation to test incoming postback
export const useTestIncomingPostbackMutation = (
    options: UseMutationOptions<
        TestConnectionSequenceStepResponse,
        AxiosError<ApiErrorResponse>,
        { incomingPostbackId: string; request: TestIncomingPostbackRequest }
    > = {}
) => {
    return useMutationApi<TestConnectionSequenceStepResponse, { incomingPostbackId: string; request: TestIncomingPostbackRequest }>({
        mutationKey: ["testIncomingPostback"],
        mutationFn: ({ incomingPostbackId, request }) => incomingPostbackApi.test(incomingPostbackId, request),
        ...options,
    });
};
